import axios from "axios";
import { InfoCircle } from "iconsax-react";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import Select from "react-select";
import { commonConfigs, urls } from "../../../../../config";
import beveatApiStaging from "../../../../api/staging_beveat_api.js";
import beveatApiProduction from "../../../../api/production_beveat_api.js";
import { TRAINING_PROVIDER_SYSCODE, TrainingProviderSyscodeString } from "../../../../constants/training_provider_syscode.jsx";

const errorIcon = (
  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
    <InfoCircle className="h-5 w-5 text-red-500" aria-hidden="true" />
  </div>
);

export default function CreateAdministratorModal({ setOpenModal, refreshData }) {
  const [isDisabled, setIsDisabled] = useState(false);
  const [roleOptions, setRoleOptions] = useState([]);
  const [trainingProviderOptions, setTrainingProviderOptions] = useState([]);
  const [loadData, setLoadData] = useState(true);

  const {
    register,
    formState: { errors },
    handleSubmit,
    control,
  } = useForm({});

  let getAllAccessRolesUrl = ``;
  let createAdministratorUrl = ``;
  let apiHeaders;
  let apiInstance;

  if (process.env.REACT_APP_ENV === "staging") {
    createAdministratorUrl = urls.baseUrl + commonConfigs.apiUrls.createAdministrator();
    getAllAccessRolesUrl = urls.baseUrl + commonConfigs.apiUrls.getAllAccessRoles();
    apiHeaders = commonConfigs.apiHeader;
    apiInstance = beveatApiStaging;
  } else {
    createAdministratorUrl = urls.prodUrl + commonConfigs.apiUrls.createAdministrator();
    getAllAccessRolesUrl = urls.prodUrl + commonConfigs.apiUrls.getAllAccessRoles();
    apiHeaders = commonConfigs.prodApiHeader;
    apiInstance = beveatApiProduction;
  }

  function formatData(data) {
    return {
      account_email: data.account_email,
      account_mobile: parseInt(data.account_mobile),
      role_id: data.role_id.value,
      training_provider_id: data.training_provider_id?.value || null,
    };
  }

  const onSubmit = (data) => {
    setIsDisabled(true);

    const payload = formatData(data);
    console.log("Payload:", payload);
    
    apiInstance
      .post(createAdministratorUrl, payload, { headers: apiHeaders })
      .then((res) => {
        try {
          if (res.data.status !== "success") {
            alert("Error creating administrator");
          } else {
            alert("Administrator Created Successfully");
            if (refreshData) refreshData();
            setOpenModal(false);
          }
        } catch (err) {
          console.log(err);
        } finally {
          setIsDisabled(false);
        }
      })
      .catch((err) => {
        alert(`Error: ${err.response?.data?.message || err.message}`);
        setIsDisabled(false);
      });
  };

  useEffect(() => {
    async function getAccessRoles() {
      try {
        const res = await apiInstance.get(getAllAccessRolesUrl, { headers: apiHeaders });
        const roles = res.data.result.data;
        
        const formattedRoles = roles.map((role) => ({
          value: role.role_id,
          label: role.role_name,
        }));
        
        setRoleOptions(formattedRoles);
      } catch (err) {
        console.log("Error fetching roles:", err);
      }
    }

    const trainingProviders = Object.entries(TrainingProviderSyscodeString)
      .map(([id, name]) => ({
        value: parseInt(id),
        label: name,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));

    setTrainingProviderOptions(trainingProviders);

    if (loadData) {
      getAccessRoles();
      setLoadData(false);
    }
  }, [loadData]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-x-hidden overflow-y-auto outline-none focus:outline-none">
      {/* Backdrop/Overlay */}
      <div
        className="fixed inset-0 bg-black opacity-40"
        onClick={() => setOpenModal(false)}
      ></div>

      {/* Modal Content */}
      <div className="relative w-full max-w-2xl mx-auto my-6 z-50">
        <div className="border-0 rounded-2xl shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none p-8">
          <div className="flex items-start justify-between mb-4">
            <h3 className="text-xl font-semibold text-gray-900">Create Administrator</h3>
            <button
              className="p-1 ml-auto bg-transparent border-0 text-gray-400 float-right text-3xl leading-none font-semibold outline-none focus:outline-none"
              onClick={() => setOpenModal(false)}
            >
              ×
            </button>
          </div>

          <form onSubmit={handleSubmit(onSubmit)}>
            {/* Email Input */}
            <div className="mb-6">
              <label htmlFor="account_email" className="block text-sm font-medium text-gray-600 mb-2 text-left">
                Email
              </label>
              <div className="relative rounded-md shadow-sm text-black">
                <input
                  type="email"
                  name="account_email"
                  id="account_email"
                  className={
                    !errors.account_email
                      ? "block w-full pr-10 border-gray-300 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md bg-white text-black"
                      : "block w-full pr-10 border-red-300 text-red-900 placeholder-red-900 focus:ring-red-500 focus:border-red-500 sm:text-sm rounded-md"
                  }
                  placeholder="Enter Email"
                  {...register("account_email", { 
                    required: "Email is required",
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: "Invalid email address"
                    }
                  })}
                />
                {errors.account_email && errorIcon}
              </div>
              {errors.account_email && (
                <p className="text-red-500 text-xs mt-1 text-left">{errors.account_email.message}</p>
              )}
            </div>

            {/* Mobile Number Input */}
            <div className="mb-6">
              <label htmlFor="account_mobile" className="block text-sm font-medium text-gray-600 mb-2 text-left">
                Mobile Number
              </label>
              <div className="relative rounded-md shadow-sm text-black">
                <input
                  type="text"
                  name="account_mobile"
                  id="account_mobile"
                  className={
                    !errors.account_mobile
                      ? "block w-full pr-10 border-gray-300 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md bg-white text-black"
                      : "block w-full pr-10 border-red-300 text-red-900 placeholder-red-900 focus:ring-red-500 focus:border-red-500 sm:text-sm rounded-md"
                  }
                  placeholder="Enter Mobile Number"
                  {...register("account_mobile", { 
                    required: "Mobile number is required",
                    pattern: {
                      value: /^[0-9]{8}$/,
                      message: "Mobile number must be 8 digits"
                    }
                  })}
                />
                {errors.account_mobile && errorIcon}
              </div>
              {errors.account_mobile && (
                <p className="text-red-500 text-xs mt-1 text-left">{errors.account_mobile.message}</p>
              )}
            </div>

            {/* Role Select */}
            <div className="mb-6">
              <label htmlFor="role_id" className="block text-sm font-medium text-gray-600 mb-2 text-left">
                Access Role
              </label>
              <div className="text-black">
                <Controller
                  name="role_id"
                  control={control}
                  rules={{ required: "Role is required" }}
                  render={({ field }) => (
                    <Select
                      {...field}
                      options={roleOptions}
                      isSearchable={true}
                      placeholder="Select a role"
                      className="text-sm"
                    />
                  )}
                />
                {errors.role_id && (
                  <p className="text-red-500 text-xs mt-1 text-left">{errors.role_id.message}</p>
                )}
              </div>
            </div>

            {/* Training Provider Select */}
            <div className="mb-8">
              <label htmlFor="training_provider_id" className="block text-sm font-medium text-gray-600 mb-2 text-left">
                Training Provider (Optional)
              </label>
              <div className="text-black">
                <Controller
                  name="training_provider_id"
                  control={control}
                  rules={{ required: false }}
                  render={({ field }) => (
                    <Select
                      {...field}
                      options={trainingProviderOptions}
                      isSearchable={true}
                      isClearable={true}
                      placeholder="Select a training provider"
                      className="text-sm"
                    />
                  )}
                />
                {errors.training_provider_id && (
                  <p className="text-red-500 text-xs mt-1 text-left">{errors.training_provider_id.message}</p>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-center space-x-4">
              <button
                type="button"
                className="hover:bg-red-500 text-red-500 hover:text-white border border-red-500 font-bold py-2 px-10 rounded-full transition-colors"
                onClick={() => setOpenModal(false)}
                disabled={isDisabled}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="hover:bg-blue-500 text-blue-500 hover:text-white border border-blue-500 font-bold py-2 px-10 rounded-full transition-colors"
                disabled={isDisabled}
              >
                {isDisabled ? "Creating..." : "Create"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}